import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eraser, Loader2, PenLine, Check } from "lucide-react";
import { toast } from "sonner";

export function SignaturePad({ title, value, onChange, testId }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const [name, setName] = useState(value?.name || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0F172A";
  }, []);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: ((t.clientX - rect.left) / rect.width) * canvas.width, y: ((t.clientY - rect.top) / rect.height) * canvas.height };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    hasDrawn.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const end = () => (drawing.current = false);

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
  };

  const save = async () => {
    if (!hasDrawn.current) return toast.error("Please sign first");
    if (!name.trim()) return toast.error("Please enter the signatory name");
    setSaving(true);
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
      const fd = new FormData();
      fd.append("file", blob, "signature.png");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ file_id: data.id, name: name.trim(), signed_at: new Date().toISOString() });
      toast.success(`${title} signature saved`);
    } catch (e) {
      toast.error("Could not save signature");
    } finally {
      setSaving(false);
    }
  };

  if (value?.file_id) {
    return (
      <div className="rounded-md border border-accent/40 bg-accent/[0.04] p-4" data-testid={`${testId}-saved`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-bold text-accent">
            <Check className="h-4 w-4" /> {title} signed
          </div>
          <Button variant="ghost" size="sm" onClick={() => onChange(null)} className="text-slate-500">
            Re-sign
          </Button>
        </div>
        <img src={fileUrl(value.file_id)} alt="signature" className="mt-2 h-20 rounded bg-white object-contain p-1" />
        <p className="mt-1 text-xs text-slate-500">
          {value.name} · {new Date(value.signed_at).toLocaleString("en-GB")}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 p-4">
      <Label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500">
        <PenLine className="h-4 w-4" /> {title}
      </Label>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Signatory full name"
        className="field-lg mt-2"
        data-testid={`${testId}-name`}
      />
      <canvas
        ref={canvasRef}
        width={600}
        height={180}
        className="mt-2 w-full touch-none rounded-md border border-dashed border-slate-300 bg-white"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid={`${testId}-canvas`}
      />
      <div className="mt-2 flex gap-2">
        <Button type="button" variant="outline" onClick={clear} className="border-slate-300 font-bold">
          <Eraser className="mr-2 h-4 w-4" /> Clear
        </Button>
        <Button type="button" onClick={save} disabled={saving} className="bg-accent font-bold hover:bg-accent/90" data-testid={`${testId}-save`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save signature"}
        </Button>
      </div>
    </div>
  );
}
